import { api } from '../api';

/**
 * "Adherentes" en la UI = legajos en estado `legajo_bis` en el backend.
 * Son los que quedaron bloqueados al cerrar un padrón nuevo porque el
 * número de legajo coincidía con un legajo existente que ya estaba en
 * flujo avanzado (en_validacion / validado_por_titular / en_alta_binary /
 * dado_de_alta / rechazado_binary / rechazado_medicus), o porque el grupo
 * llegó sin titular y su legajo ya existía (bis_sin_titular).
 */

/**
 * Detalle de un adicional del legajo bis: datos actuales + los campos que el
 * validador del padrón marca con error — los que hay que completar en el
 * modal antes de poder agregarlo al legajo.
 */
export interface AdicionalBis {
  asociadoId: string;
  legajo: string;
  nombres: string;
  apellSolt: string;
  documento: string;
  parentesco: string;
  sexo: string;
  estadoCivil: string;
  cuil: string;
  fechaNacimiento: string;
  nacionalidad: string;
  tipoDoc: string;
  domicilio: string;
  numero: string;
  piso: string;
  depto: string;
  cp: string;
  areaCelular: string;
  numeroCelular: string;
  email: string;
  estado: 'ok' | 'error';
  camposConError: string[];
}

export interface Adherente {
  id: string;
  numero: string;
  empresaId: string;
  padronId: string;
  titularAsociadoId: string;
  titularNombre: string;
  titularApellido: string;
  titularDocumento: string;
  familiaresAsociadoIds: string[];
  cantidadPersonas: number;
  adicionales: AdicionalBis[];
  /**
   * Estado VIVO del legajo destino — `legajoBisOrigen.estadoOriginal` es un
   * snapshot de cuando se detectó el bis y puede haber quedado viejo (ej. el
   * destino se dio de alta después). La UI decide con este.
   */
  estadoDestino: string;
  /**
   * Activación por el wizard del titular (`bisActivacion.estado`). Sólo
   * `validado` habilita el envío a Binary — lo exige `cargarBisParaEnvio` en el
   * server, así que la UI tiene que mirarlo antes de ofrecer "Enviar".
   */
  activacionEstado?: string;
  legajoBisOrigen?: {
    legajoIdOriginal: string;
    numero: string;
    estadoOriginal: string;
    padronIdOriginal: string;
    detectadoAt: string;
  };
  /**
   * Si está seteado, el bis ya se exportó al alta masiva de Binary (alta
   * complementaria). Sigue en estado `legajo_bis`: se vincula al titular
   * automáticamente cuando Binary active al adicional (cron syncBisMerge).
   */
  enviadoABinary?: {
    at: string;
    por?: string;
    batch?: string;
    archivo?: string;
    asociadoIds?: string[];
  } | null;
  /**
   * Último rebote de Binary al enviar los integrantes (VEN-561). El bis sigue
   * pendiente y se puede reintentar; el motivo queda acá para que se vea. Se
   * limpia cuando el envío sale bien.
   */
  binaryError?: {
    message: string;
    at: string;
    por?: string;
    origen?: string;
    intentos?: number;
  } | null;
  /**
   * VEN-615: el bis tiene un familiar con parentesco 13 (Adherente). El server
   * lo rechaza siempre antes de llegar a Binary y ya no lo cuenta como enviable:
   * hay que quitar al adherente del legajo antes de poder mandarlo.
   */
  tieneAdherente?: boolean;
  /** Último mail de activación al titular destino; ausente si nunca salió. */
  invitacionEnviadaAt?: string;
  invitacionEnviadaA?: string;
  /**
   * true si el padrón que trajo el bis ya se validó. El mail de activación sale
   * recién al validarlo: con el padrón pendiente no hay nada que reenviar.
   */
  padronValidado?: boolean;
  /**
   * Email que tiene HOY el titular del legajo destino (a donde iría un
   * reenvío). Solo viene para SUPERADMIN; '' si no tiene email cargado.
   */
  emailTitularDestino?: string;
  createdAt: string;
}

export interface ListarAdherentesResp {
  items: Adherente[];
  total: number;
  /** Personas (titular + familiares) en los bis del listado. */
  totalPersonas: number;
  /** Bis activados por el wizard (Fase 2, listos para enviar a Binary). */
  totalActivados: number;
  /**
   * Bis que se pueden enviar YA por API en TODO el scope (no solo la página):
   * destino dado_de_alta + activado por el titular + no enviados. Es el número
   * del botón "Enviar todos a Binary"; el server lo calcula con el mismo
   * criterio que `adherenteEsEnviable()`.
   */
  totalEnviables: number;
  /** Cuántos bis están marcados como enviados a Binary (ocultos por defecto). */
  totalEnviados: number;
  /** Personas en los bis marcados como enviados a Binary. */
  totalPersonasEnviados: number;
  /** Bis del scope (no enviados) con un rebote de Binary guardado (VEN-561). */
  totalRechazados?: number;
  page: number;
  pageSize: number;
}

export async function listarAdherentes(
  opts: {
    page?: number;
    pageSize?: number;
    q?: string;
    incluirEnviados?: boolean;
    /** Solo los bis con un rebote de Binary guardado (VEN-561). */
    soloRechazados?: boolean;
  } = {},
): Promise<ListarAdherentesResp> {
  const { data } = await api.get<ListarAdherentesResp>('/legajos/bis', {
    params: {
      page: opts.page ?? 1,
      pageSize: opts.pageSize ?? 10,
      ...(opts.q?.trim() ? { q: opts.q.trim() } : {}),
      ...(opts.incluirEnviados ? { incluirEnviados: 'true' } : {}),
      ...(opts.soloRechazados ? { soloRechazados: 'true' } : {}),
    },
  });
  // Normalización defensiva del contrato: un backend desactualizado (sin el
  // rework v2) responde sin `adicionales`/`camposConError`, y la tabla los
  // mapea sin guardas → la vista entera tira "Cannot read properties of
  // undefined (reading 'map')". Garantizamos los arrays acá, en el único punto
  // donde entran los datos externos, para degradar en vez de romper.
  return {
    ...data,
    totalPersonas: data.totalPersonas ?? 0,
    totalActivados: data.totalActivados ?? 0,
    totalEnviables: data.totalEnviables ?? 0,
    totalEnviados: data.totalEnviados ?? 0,
    totalPersonasEnviados: data.totalPersonasEnviados ?? 0,
    items: (data.items ?? []).map((it) => ({
      ...it,
      adicionales: (it.adicionales ?? []).map((a) => ({
        ...a,
        camposConError: a.camposConError ?? [],
      })),
    })),
  };
}

/**
 * Estados del legajo destino en los que NO se puede agregar el adherente (el
 * titular está en medio del flujo del portal). En esos casos el caso es solo
 * informativo. Debe quedar alineado con `ESTADOS_BIS_NO_EDITABLE` del backend.
 */
export const ESTADOS_BIS_NO_EDITABLE = ['en_validacion', 'validado_por_titular', 'en_alta_binary'];

/** Estado vivo del destino, con fallback al snapshot para datos viejos. */
function estadoDestinoDe(a: Adherente): string {
  return a.estadoDestino || (a.legajoBisOrigen?.estadoOriginal ?? '');
}

/**
 * Motivo por el que el adherente NO se puede agregar, o null si se puede:
 *  - 'en_proceso': el titular está en medio del flujo (se libera solo).
 *  - 'dado_de_alta': el alta del grupo ya se procesó en Binary; el caso queda
 *    en la lista (al final) hasta que se defina el procedimiento (03/06).
 */
export function motivoNoAgregable(a: Adherente): 'en_proceso' | 'dado_de_alta' | null {
  const est = estadoDestinoDe(a);
  if (est === 'dado_de_alta') return 'dado_de_alta';
  if (ESTADOS_BIS_NO_EDITABLE.includes(est)) return 'en_proceso';
  return null;
}

/** true si los integrantes se pueden agregar al legajo destino. */
export function adherenteEsAgregable(a: Adherente): boolean {
  return motivoNoAgregable(a) === null;
}

/**
 * true si el bis se puede ENVIAR a Binary por API ("Alta Integrantes").
 *
 * Son DOS condiciones, las mismas que valida `cargarBisParaEnvio` en el server:
 *  1. el titular destino ya está afiliado (`dado_de_alta`), y
 *  2. el bis está ACTIVADO por el wizard del titular (`activacionEstado`
 *     = 'validado'), que es lo que confirma datos + DDJJ del nuevo integrante.
 *
 * Antes sólo se miraba (1), así que la vista ofrecía "Enviar" en bis sin
 * activar y el envío los rechazaba con BIS_NO_ACTIVADO. `activacionEstado` es
 * opcional por compatibilidad: si la API todavía no lo manda, no bloqueamos
 * (el server sigue siendo el que decide).
 *
 * ⚠️ Esa rama de compatibilidad (`=== undefined`) NO se debe espejar del lado
 * del server: ahí se lee Mongo directo, donde el bis sin activar simplemente no
 * tiene `bisActivacion`, y aceptarlo cuenta como enviable algo que va a rebotar.
 * Pasó con `totalEnviables`: el botón ofrecía 25 y solo 3 eran enviables. En el
 * server el criterio es estricto (`bisActivacion.estado === 'validado'`, ver
 * `ENVIABLE_MATCH` en `legajos.repository.ts`); acá nunca se cumple porque el
 * server mapea el ausente a `'pendiente'`.
 */
export function adherenteEsEnviable(a: Adherente): boolean {
  if (motivoNoAgregable(a) !== 'dado_de_alta') return false;
  // VEN-615: con un adherente adentro el server lo rechaza siempre (misma regla
  // que `ENVIABLE_MATCH`); ofrecer "Enviar" solo sumaría otro intento fallido.
  if (a.tieneAdherente === true) return false;
  return a.activacionEstado === undefined || a.activacionEstado === 'validado';
}

/** true si el bis espera que el titular complete el wizard de activación. */
export function adherenteEsperaActivacion(a: Adherente): boolean {
  return (
    motivoNoAgregable(a) === 'dado_de_alta' &&
    a.activacionEstado !== undefined &&
    a.activacionEstado !== 'validado'
  );
}

// ---------------------------------------------------------------------------
// Alta Integrantes — envío a Binary por API (alta-familiar).
// ---------------------------------------------------------------------------

/** Resultado del envío de UN bis. */
export interface EnvioBisResultado {
  estado: 'ok' | 'rechazado_binary' | 'error' | 'omitido';
  bisId: string;
  numero?: string;
  legajoDestinoId?: string;
  titular?: { apellido: string; nombres: string; documento: string };
  errores?: string[];
  binaryError?: string;
  integrantes?: Array<{ asociadoId: string; parentesco: string; medicard?: string; afiId?: string }>;
}

/** Fila del reporte del job en lote (molde compartido con Alta Grupos). */
export interface EnvioBisResultadoGrupo {
  legajo: string;
  titular: { apellido: string; nombres: string; documento: string };
  estado: 'ok' | 'error' | 'omitido';
  legajoId?: string;
  medicards?: Array<{ parentesco: string; documento: string; medicard: string | null }>;
  error?: string;
}

/** Resumen del job de envío en lote de bis. */
export interface EnvioBisLoteResumen {
  jobId: string;
  estado: 'corriendo' | 'terminado' | 'error';
  totalGrupos: number;
  procesados: number;
  exitosos: number;
  fallidos: number;
  omitidos: number;
  nombre?: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  resultados?: EnvioBisResultadoGrupo[];
}

/** Envía un único bis a Binary como "Alta Integrantes" (alta-familiar). */
export async function enviarAdherente(bisId: string): Promise<EnvioBisResultado> {
  const { data } = await api.post<EnvioBisResultado>(`/legajos/bis/${bisId}/enviar`);
  return data;
}

/** Mismo mínimo entre envíos que `REENVIO_INVITACION_MIN_MS` del server. */
export const REENVIO_INVITACION_MIN_MS = 2 * 60 * 1000;

export interface ReenvioInvitacionBis {
  bisId: string;
  numero: string;
  enviadoA: string;
  enviadoAt: string;
}

/**
 * Reenvía el mail de activación al email ACTUAL del titular destino (mismo
 * link). Solo SUPERADMIN: el server lo corta con 403 para cualquier otro rol.
 */
export async function reenviarInvitacionAdherente(bisId: string): Promise<ReenvioInvitacionBis> {
  const { data } = await api.post<ReenvioInvitacionBis>(`/legajos/bis/${bisId}/reenviar-invitacion`);
  return data;
}

/** Envía un lote de bis a Binary; devuelve el jobId para hacer polling. */
export async function enviarAdherentesLote(bisIds: string[]): Promise<EnvioBisLoteResumen> {
  const { data } = await api.post<EnvioBisLoteResumen>('/legajos/bis/enviar', { bisIds });
  return data;
}

/**
 * Envía TODOS los bis enviables del scope (no los de la página). Los ids los
 * resuelve el server con el mismo criterio del contador `totalEnviables`; `q`
 * es el filtro que el operador tiene puesto en la pantalla.
 */
export async function enviarTodosLosAdherentes(q?: string): Promise<EnvioBisLoteResumen> {
  const { data } = await api.post<EnvioBisLoteResumen>('/legajos/bis/enviar', {
    todos: true,
    ...(q?.trim() ? { q: q.trim() } : {}),
  });
  return data;
}

/** Progreso + resultados de un job de envío "Alta Integrantes". */
export async function getEnvioAdherentesJob(jobId: string): Promise<EnvioBisLoteResumen> {
  const { data } = await api.get<EnvioBisLoteResumen>(`/legajos/bis/enviar/${jobId}`);
  return data;
}

/** Payload del 422 del backend cuando algún integrante quedó con datos inválidos. */
export interface DatosIncompletosError {
  code: 'DATOS_INCOMPLETOS';
  message: string;
  integrantes: Array<{
    asociadoId: string;
    nombre: string;
    errores: Array<{ field: string; rule: string; message: string }>;
  }>;
}

/**
 * Agrega los integrantes del legajo_bis al legajo destino y elimina el bis.
 * `integrantes` lleva los campos completados por el operador para cada
 * adicional (solo los del editor de filas del padrón). El backend valida con
 * el row.validator, mueve a cada uno del padrón en que llegó suelto al padrón
 * del titular y recién ahí los agrega. Falla con 409 si el destino quedó en
 * un estado no editable, o 422 (DATOS_INCOMPLETOS) si falta completar datos.
 */
export async function agregarAdherenteAlLegajo(
  id: string,
  integrantes?: Array<{ asociadoId: string; campos?: Record<string, string> }>,
): Promise<{ ok: true; legajoDestinoId: string; integrantesAgregados: number }> {
  const { data } = await api.post<{
    ok: true;
    legajoDestinoId: string;
    integrantesAgregados: number;
  }>(`/legajos/bis/${id}/agregar`, integrantes ? { integrantes } : {});
  return data;
}

// ---------- Últimas altas de integrantes (Alta Integrantes) ----------

export interface UltimaAltaIntegrante {
  /** Estado que hoy tiene en Binary: 'A' activo · 'B' baja · null si no se pudo leer. */
  estadoBinary?: string | null;
  /** Plan vigente del grupo en Binary (el familiar hereda el del titular). */
  planActual?: string | null;
  asociadoId: string;
  numero: string;
  altaAt: string | null;
  integrante: string;
  documento: string;
  parentesco: string;
  medicard: string | null;
}

/** Últimas altas de INTEGRANTES sumados a un titular ya afiliado. */
export async function getUltimasAltasIntegrantes(
  page: number,
  pageSize: number,
): Promise<{ items: UltimaAltaIntegrante[]; total: number; page: number; pageSize: number }> {
  const { data } = await api.get<{
    items: UltimaAltaIntegrante[];
    total: number;
    page: number;
    pageSize: number;
  }>('/legajos/bis/ultimas-altas', { params: { page, pageSize } });
  return data;
}
